<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTheme } from '@/composables/useTheme'
import { useSidebarState } from '@/composables/useSidebarState'
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts'
import { useQueryErrorHandler } from '@/composables/useQueryErrorHandler'
import AppSidebar from '@/layouts/_AppSidebar.vue'
import AppTopbar from '@/layouts/_AppTopbar.vue'
import AppMobileDrawer from '@/layouts/_AppMobileDrawer.vue'

const router = useRouter()
const authStore = useAuthStore()
const { mode, toggle } = useTheme()
const {
  collapsed,
  toggleCollapsed,
  mobileDrawerOpen,
  openMobileDrawer,
  closeMobileDrawer,
} = useSidebarState()

useQueryErrorHandler()

useKeyboardShortcuts({
  onNavigate(routeName) {
    if (router.hasRoute(routeName)) {
      router.push({ name: routeName })
    }
  },
  onToggleTheme() {
    toggle()
  },
  onOpenGuide() {
    if (router.hasRoute('guide')) router.push({ name: 'guide' })
  },
})

function onLogout() {
  authStore.clear()
  router.push({ name: 'login' })
}
</script>

<template>
  <div class="app-shell" :data-collapsed="collapsed ? '1' : '0'">
    <AppSidebar
      class="app-sidebar-desktop"
      :collapsed="collapsed"
      :username="authStore.username ?? ''"
      @logout="onLogout"
    />

    <div class="app-main">
      <AppTopbar
        :theme-mode="mode"
        @menu-click="openMobileDrawer"
        @collapse-toggle="toggleCollapsed"
        @theme-toggle="toggle"
      />
      <main class="app-content">
        <RouterView />
      </main>
    </div>

    <AppMobileDrawer
      :open="mobileDrawerOpen"
      :username="authStore.username ?? ''"
      @close="closeMobileDrawer"
      @logout="onLogout"
    />
  </div>
</template>

<style scoped>
.app-shell {
  display: flex;
  height: 100vh;
  background: var(--bg-2);
}
.app-sidebar-desktop {
  flex-shrink: 0;
}
.app-main {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.app-content {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: var(--sp-5);
}

@media (max-width: 767px) {
  .app-sidebar-desktop { display: none; }
  .app-content { padding: var(--sp-4); }
}
</style>
